/**
 * The score sheet for a klaverjas game played with real cards.
 *
 * Per deal there are three things to say — who played, which trump, and what
 * the playing side took — and the app works out the rest: the other team's
 * points, whether they went wet, and whether it was a pit. Roem is one tap away
 * and stays folded up, because most deals have none.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Suit } from '@shared/types/game';
import { DEAL_TOTAL, PIT_BONUS, scoreDealFromTotals } from '@shared/game-logic';
import { klaverjasStandings, klaverjasTotals } from '@shared/types/klaverjas';
import { Button, Empty, Panel, Screen } from '@/components/ui';
import { SUIT_LABELS } from '@/lib/labels';
import { SUIT_SYMBOLS } from '@shared/game-logic';
import {
  currentDealOf,
  dealIsComplete,
  selectKlaverjasGame,
  useKlaverjasScore,
} from '@/store/klaverjasScore';

const SUITS: Suit[] = ['clubs', 'hearts', 'spades', 'diamonds'];

export default function KlaverjasScore() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const game = useKlaverjasScore(selectKlaverjasGame(gameId));

  if (!game) {
    return (
      <Screen title="Klaverjassen" back="/">
        <Empty>Dit potje bestaat niet meer.</Empty>
      </Screen>
    );
  }

  const totals = klaverjasTotals(game);
  const standings = klaverjasStandings(game);
  const open = currentDealOf(game);

  return (
    <Screen
      title={game.status === 'finished' ? 'Afgelopen' : `Gift ${open?.dealNo ?? game.deals.length}`}
      back="/"
      subtitle={
        game.options.length.kind === 'deals'
          ? `${game.deals.filter((d) => d.result).length} van ${game.options.length.deals} giften · ${game.options.variant}`
          : `tot ${game.options.length.target} punten · ${game.options.variant}`
      }
    >
      <Panel title="Stand">
        <ul className="space-y-2">
          {standings.map((team) => (
            <li key={team.team} className="flex items-center gap-3">
              <span
                className={`h-8 w-1.5 rounded-full ${team.team === 0 ? 'bg-accent-500' : 'bg-slate-500'}`}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{team.names.join(' & ')}</span>
                <span className="block text-xs text-slate-500">
                  {team.played} keer gespeeld{team.nat > 0 ? `, ${team.nat}× nat` : ''}
                </span>
              </span>
              <span className="text-2xl font-bold tabular-nums">{team.total}</span>
            </li>
          ))}
        </ul>
        {totals[0] === totals[1] && game.status === 'finished' && (
          <p className="mt-2 text-center text-sm text-slate-400">Gelijkspel.</p>
        )}
      </Panel>

      {open && <DealEntry gameId={game.id} />}

      {game.deals.some((deal) => deal.result) && <Sheet gameId={game.id} />}

      {game.status === 'finished' && (
        <Button full variant="primary" onClick={() => navigate('/klaverjas/nieuw')}>
          Nog een potje
        </Button>
      )}
    </Screen>
  );
}

function DealEntry({ gameId }: { gameId: string }) {
  const game = useKlaverjasScore(selectKlaverjasGame(gameId))!;
  const setDeal = useKlaverjasScore((state) => state.setDeal);
  const confirm = useKlaverjasScore((state) => state.confirmDeal);
  const [showRoem, setShowRoem] = useState(false);

  const deal = currentDealOf(game);
  if (!deal) return null;

  const names = klaverjasStandings(game).map((team) => team.names.join(' & '));
  const ready = dealIsComplete(deal);

  // A live preview, so the table sees "nat" before committing rather than
  // being told afterwards.
  const preview =
    ready && deal.takenTeam0 !== null && deal.playingTeam !== null
      ? scoreDealFromTotals({
          playingTeam: deal.playingTeam,
          taken: [deal.takenTeam0, DEAL_TOTAL - deal.takenTeam0],
          roem: deal.roem,
          pitBonus: game.options.pitBonus,
        })
      : null;

  return (
    <Panel title={`Gift ${deal.dealNo} — ${game.seats[deal.dealerSeat]?.displayName} deelt`}>
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-sm font-medium">Wie speelt?</p>
          <div className="grid grid-cols-2 gap-2">
            {([0, 1] as const).map((team) => (
              <button
                key={team}
                type="button"
                aria-pressed={deal.playingTeam === team}
                onClick={() => setDeal(gameId, deal.dealNo, { playingTeam: team })}
                className={`min-h-12 truncate rounded-xl border px-3 text-sm transition-colors ${
                  deal.playingTeam === team
                    ? 'border-accent-500 bg-accent-500/10'
                    : 'border-white/10 bg-base-800/60'
                }`}
              >
                {names[team]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium">Troef</p>
          <div className="grid grid-cols-4 gap-2">
            {SUITS.map((suit) => (
              <button
                key={suit}
                type="button"
                aria-pressed={deal.trump === suit}
                aria-label={SUIT_LABELS[suit]}
                onClick={() => setDeal(gameId, deal.dealNo, { trump: suit })}
                className={`min-h-12 rounded-xl border text-2xl transition-colors ${
                  deal.trump === suit
                    ? 'border-accent-500 bg-accent-500/10'
                    : 'border-white/10 bg-base-800/60'
                } ${suit === 'hearts' || suit === 'diamonds' ? 'text-red-400' : 'text-slate-200'}`}
              >
                {SUIT_SYMBOLS[suit]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium">
            Punten voor {names[0]}{' '}
            <span className="font-normal text-slate-500">— de laatste slag telt mee</span>
          </p>
          {/* A field, not a NumberPad: that draws one button per value, which
              would be 163 of them. */}
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={DEAL_TOTAL}
            value={deal.takenTeam0 ?? ''}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === '') return setDeal(gameId, deal.dealNo, { takenTeam0: null });
              const clamped = Math.min(DEAL_TOTAL, Math.max(0, Number(raw) || 0));
              setDeal(gameId, deal.dealNo, { takenTeam0: clamped });
            }}
            className="min-h-14 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-center text-2xl font-semibold tabular-nums outline-none focus:border-accent-500"
          />
          {deal.takenTeam0 !== null && (
            <p className="mt-1 text-sm text-slate-400">
              {names[1]}: {DEAL_TOTAL - deal.takenTeam0}
            </p>
          )}
        </div>

        {showRoem ? (
          <div className="grid grid-cols-2 gap-3">
            {([0, 1] as const).map((team) => (
              <label key={team} className="block">
                <span className="mb-1 block truncate text-xs text-slate-500">
                  Roem {names[team]}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={500}
                  value={deal.roem[team] || ''}
                  onChange={(event) => {
                    const roem: [number, number] = [...deal.roem] as [number, number];
                    roem[team] = Math.max(0, Number(event.target.value) || 0);
                    setDeal(gameId, deal.dealNo, { roem });
                  }}
                  className="min-h-12 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-base tabular-nums outline-none focus:border-accent-500"
                />
              </label>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowRoem(true)}
            className="text-sm text-slate-500 underline underline-offset-4 hover:text-slate-300"
          >
            Er was roem
          </button>
        )}

        {preview && (
          <p
            className={`rounded-xl px-3 py-2 text-sm ${
              preview.nat
                ? 'border-l-2 border-bad bg-bad/10 text-red-100'
                : 'border-l-2 border-accent-500 bg-accent-500/10 text-slate-200'
            }`}
          >
            {preview.nat
              ? `${names[deal.playingTeam!]} gaat nat — alles naar ${names[1 - deal.playingTeam!]}.`
              : `${names[0]} ${preview.points[0]} · ${names[1]} ${preview.points[1]}`}
            {preview.pitTeam !== null && ` Pit! +${PIT_BONUS}.`}
          </p>
        )}

        <Button
          full
          variant="primary"
          disabled={!ready}
          onClick={() => confirm(gameId, deal.dealNo)}
        >
          {ready ? 'Gift vastleggen' : 'Vul de gift in'}
        </Button>
      </div>
    </Panel>
  );
}

function Sheet({ gameId }: { gameId: string }) {
  const game = useKlaverjasScore(selectKlaverjasGame(gameId))!;
  const reopen = useKlaverjasScore((state) => state.reopenDeal);
  const scored = game.deals.filter((deal) => deal.result);

  const running: Array<[number, number]> = [];
  scored.reduce<[number, number]>(
    (totals, deal) => {
      const next: [number, number] = [
        totals[0] + deal.result!.points[0],
        totals[1] + deal.result!.points[1],
      ];
      running.push(next);
      return next;
    },
    [0, 0]
  );

  return (
    <Panel title="Telblad">
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th className="pb-1 font-normal">#</th>
            <th className="pb-1 font-normal">troef</th>
            <th className="pb-1 text-right font-normal">wij</th>
            <th className="pb-1 text-right font-normal">zij</th>
            <th className="pb-1" />
          </tr>
        </thead>
        <tbody>
          {scored.map((deal, index) => (
            <tr key={deal.dealNo} className="border-t border-white/5">
              <td className="py-1.5 text-slate-500">{deal.dealNo}</td>
              <td className="py-1.5">
                <span
                  className={
                    deal.trump === 'hearts' || deal.trump === 'diamonds'
                      ? 'text-red-400'
                      : 'text-slate-300'
                  }
                >
                  {deal.trump ? SUIT_SYMBOLS[deal.trump] : '—'}
                </span>
                {/* A dot marks who chose it; nat is the story of the evening. */}
                <span className="ml-1 text-xs text-slate-500">
                  {deal.playingTeam === 0 ? 'wij' : 'zij'}
                  {deal.result?.nat && <span className="ml-1 text-bad">nat</span>}
                  {deal.result?.pitTeam !== null && (
                    <span className="ml-1 text-accent-400">pit</span>
                  )}
                </span>
              </td>
              <td className="py-1.5 text-right">
                {deal.result!.points[0]}
                <span className="ml-1 text-xs text-slate-500">{running[index][0]}</span>
              </td>
              <td className="py-1.5 text-right">
                {deal.result!.points[1]}
                <span className="ml-1 text-xs text-slate-500">{running[index][1]}</span>
              </td>
              <td className="py-1.5 pl-2 text-right">
                <button
                  type="button"
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Gift ${deal.dealNo} opnieuw invullen? Alles daarna wordt gewist.`
                      )
                    )
                      return;
                    reopen(gameId, deal.dealNo);
                  }}
                  className="text-xs text-slate-600 hover:text-white"
                  aria-label={`Gift ${deal.dealNo} corrigeren`}
                >
                  ✎
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
